import * as XLSX from "xlsx";

export type PortalPayrollFieldKey =
  | "rut"
  | "fullName"
  | "names"
  | "lastNames"
  | "firstName"
  | "middleName"
  | "lastName1"
  | "lastName2"
  | "birthDate"
  | "gender"
  | "maritalStatus"
  | "nationality"
  | "email"
  | "phone"
  | "address"
  | "locality"
  | "children"
  | "healthSystem"
  | "afp"
  | "bank"
  | "accountType"
  | "accountNumber"
  | "tshirtSize"
  | "pantSize"
  | "shoeSize"
  | "emergencyName"
  | "emergencyPhone"
  | "emergencyRelationship"
  | "hasDisease"
  | "disease"
  | "hasDisability"
  | "disability";

export interface PortalSystemColumn {
  key: PortalPayrollFieldKey;
  label: string;
  description?: string;
  required?: boolean;
  aliases: string[];
}

export const PORTAL_SYSTEM_COLUMNS: PortalSystemColumn[] = [
  { key: "rut", label: "RUT", required: true, aliases: ["rut", "run", "dni", "documento", "cedula", "identificacion"] },
  { key: "fullName", label: "Nombre completo", description: "Primer y segundo nombre + primer y segundo apellido", aliases: ["nombre completo", "nombres y apellidos", "nombre candidato", "nombre y apellido"] },
  { key: "names", label: "Nombres", description: "Primer nombre + segundo nombre", aliases: ["nombre", "nombres", "nombres completos", "primer y segundo nombre"] },
  { key: "lastNames", label: "Apellidos", description: "Primer apellido + segundo apellido", aliases: ["apellido", "apellidos", "apellidos completos", "primer y segundo apellido"] },
  { key: "firstName", label: "Primer nombre", aliases: ["primer nombre", "nombre 1"] },
  { key: "middleName", label: "Segundo nombre", aliases: ["segundo nombre", "nombre 2"] },
  { key: "lastName1", label: "Primer apellido", aliases: ["primer apellido", "apellido paterno", "apellido 1"] },
  { key: "lastName2", label: "Segundo apellido", aliases: ["segundo apellido", "apellido materno", "apellido 2"] },
  { key: "birthDate", label: "Fecha de nacimiento", aliases: ["fecha nacimiento", "fecha de nacimiento", "nacimiento", "f nacimiento"] },
  { key: "gender", label: "Género", aliases: ["genero", "sexo"] },
  { key: "maritalStatus", label: "Estado civil", aliases: ["estado civil"] },
  { key: "nationality", label: "Nacionalidad", aliases: ["nacionalidad", "pais", "pais de origen"] },
  { key: "email", label: "Correo", aliases: ["correo", "email", "e mail", "correo electronico"] },
  { key: "phone", label: "Teléfono", aliases: ["telefono", "fono", "celular", "movil", "whatsapp"] },
  { key: "address", label: "Dirección", aliases: ["direccion", "domicilio"] },
  { key: "locality", label: "Comuna", aliases: ["comuna", "localidad", "ciudad"] },
  { key: "children", label: "Número de hijos", aliases: ["numero de hijos", "n hijos", "hijos", "cargas familiares", "total hijos"] },
  { key: "healthSystem", label: "Sistema de salud", aliases: ["sistema de salud", "salud", "prevision salud", "isapre"] },
  { key: "afp", label: "AFP", aliases: ["afp", "prevision", "sistema previsional"] },
  { key: "bank", label: "Banco", aliases: ["banco", "institucion bancaria"] },
  { key: "accountType", label: "Tipo de cuenta bancaria", aliases: ["tipo de cuenta", "tipo cuenta", "tipo cuenta bancaria"] },
  { key: "accountNumber", label: "Número de cuenta bancaria", aliases: ["numero de cuenta", "n cuenta", "cuenta bancaria"] },
  { key: "tshirtSize", label: "Talla de polera", aliases: ["talla polera", "polera", "camiseta", "talla camiseta"] },
  { key: "pantSize", label: "Talla de pantalón", aliases: ["talla pantalon", "pantalon"] },
  { key: "shoeSize", label: "Número de calzado", aliases: ["numero calzado", "calzado", "zapatos", "talla zapatos", "talla calzado"] },
  { key: "emergencyName", label: "Nombre contacto de emergencia", aliases: ["nombre contacto emergencia", "contacto emergencia", "contacto de emergencia"] },
  { key: "emergencyPhone", label: "Teléfono contacto de emergencia", aliases: ["telefono contacto emergencia", "fono emergencia", "telefono emergencia"] },
  { key: "emergencyRelationship", label: "Parentesco contacto de emergencia", aliases: ["parentesco contacto emergencia", "parentesco emergencia"] },
  { key: "hasDisease", label: "Enfermedad", aliases: ["enfermedad", "tiene enfermedad", "enfermedades"] },
  { key: "disease", label: "Detalle de enfermedad", aliases: ["detalle enfermedad", "descripcion enfermedad", "observacion enfermedad"] },
  { key: "hasDisability", label: "Discapacidad", aliases: ["discapacidad", "tiene discapacidad"] },
  { key: "disability", label: "Detalle de discapacidad", aliases: ["detalle discapacidad", "descripcion discapacidad", "observacion discapacidad"] },
];

export interface PortalSheetData {
  headers: string[];
  rows: Record<string, string | number | boolean>[];
  headerRow: number;
}

export interface PortalResultSummary {
  total: number;
  complete: number;
  incomplete: number;
  missing: number;
  invalid: number;
}

export const normalizePortalHeader = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const hasCellValue = (value: unknown): boolean =>
  value !== null && value !== undefined && String(value).trim() !== "";

export const findPortalHeaderRow = (matrix: unknown[][]): number => {
  const candidates = matrix.slice(0, 30);
  const knownHeaders = new Set(
    PORTAL_SYSTEM_COLUMNS.flatMap((column) => [column.label, ...column.aliases]).map(normalizePortalHeader)
  );
  let bestIndex = -1;
  let bestScore = -1;
  candidates.forEach((row, index) => {
    if (!Array.isArray(row)) return;
    const values = row.filter(hasCellValue);
    if (values.length === 0) return;
    const unique = new Set(values.map(normalizePortalHeader)).size;
    const textValues = values.filter((value) => typeof value === "string").length;
    const recognizedHeaders = values.filter((value) => knownHeaders.has(normalizePortalHeader(value))).length;
    const dataShapedValues = values.filter((value) => {
      const text = String(value).trim();
      return (
        typeof value === "number" ||
        value instanceof Date ||
        /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/.test(text) ||
        /^\d{6,9}-[0-9kK]$/.test(text.replace(/\./g, "")) ||
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
      );
    }).length;
    const score = recognizedHeaders * 50 + values.length * 10 + unique + textValues - dataShapedValues * 20;
    if (score > bestScore) {
      bestIndex = index;
      bestScore = score;
    }
  });
  return bestIndex;
};

const serializeCell = (value: unknown): string | number | boolean => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const extractPortalSheetAtHeader = (
  workbook: XLSX.WorkBook,
  sheetName: string,
  forcedHeaderIndex?: number
): PortalSheetData => {
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) throw new Error("No se encontró la hoja seleccionada.");
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
    header: 1,
    defval: "",
    raw: true,
    blankrows: false,
  }) as unknown[][];
  const headerIndex = forcedHeaderIndex ?? findPortalHeaderRow(matrix);
  if (headerIndex < 0) throw new Error("No se encontraron columnas en la hoja seleccionada.");

  const seen = new Map<string, number>();
  const headerCells = (matrix[headerIndex] ?? []).map((value, columnIndex) => {
    if (!hasCellValue(value)) return undefined;
    const base = String(value).trim();
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return { columnIndex, name: count === 1 ? base : `${base} (${count})` };
  }).filter(Boolean) as Array<{ columnIndex: number; name: string }>;
  if (headerCells.length === 0) throw new Error("No se encontraron columnas en la hoja seleccionada.");

  const rows = matrix.slice(headerIndex + 1).map((matrixRow) => {
    const row: Record<string, string | number | boolean> = Object.create(null);
    headerCells.forEach(({ columnIndex, name }) => {
      row[name] = serializeCell(matrixRow?.[columnIndex]);
    });
    return row;
  }).filter((row) => Object.values(row).some(hasCellValue));

  return { headers: headerCells.map(({ name }) => name), rows, headerRow: headerIndex + 1 };
};

export const extractPortalSheet = (workbook: XLSX.WorkBook, sheetName: string): PortalSheetData =>
  extractPortalSheetAtHeader(workbook, sheetName);

export const extractPortalResultSheet = (workbook: XLSX.WorkBook): PortalSheetData => {
  const resultSheet = workbook.SheetNames.find((name) => normalizePortalHeader(name) !== "resumen");
  if (!resultSheet) throw new Error("El Excel generado no contiene una hoja de resultados.");
  return extractPortalSheetAtHeader(workbook, resultSheet, 0);
};

export const readPortalWorkbook = async (file: File): Promise<XLSX.WorkBook> => {
  const buffer = await file.arrayBuffer();
  return XLSX.read(buffer, { type: "array", cellDates: true });
};

export const readPortalResultWorkbook = async (blob: Blob): Promise<PortalSheetData> => {
  const buffer = await blob.arrayBuffer();
  return extractPortalResultSheet(XLSX.read(buffer, { type: "array", cellDates: true }));
};

export type PortalResultFilter = "all" | "complete" | "incomplete" | "missing" | "invalid";

const getResultStatusHeader = (result: PortalSheetData): string | undefined =>
  result.headers.find((header) => normalizePortalHeader(header) === "estado registro");

export const summarizePortalResult = (result: PortalSheetData): PortalResultSummary => {
  const statusHeader = getResultStatusHeader(result);
  const statuses = statusHeader
    ? result.rows.map((row) => normalizePortalHeader(row[statusHeader]))
    : [];
  const count = (status: string): number => statuses.filter((value) => value === status).length;
  return {
    total: result.rows.length,
    complete: count("completo"),
    incomplete: count("incompleto"),
    missing: count("inexistente"),
    invalid: count("rut invalido") + count("datos invalidos"),
  };
};

export const filterPortalResultRows = (
  result: PortalSheetData,
  filter: PortalResultFilter
): PortalSheetData["rows"] => {
  if (filter === "all") return result.rows;

  const statusHeader = getResultStatusHeader(result);
  if (!statusHeader) return [];

  const acceptedStatuses: Record<Exclude<PortalResultFilter, "all">, string[]> = {
    complete: ["completo"],
    incomplete: ["incompleto"],
    missing: ["inexistente"],
    invalid: ["rut invalido", "datos invalidos"],
  };
  const accepted = new Set(acceptedStatuses[filter]);
  return result.rows.filter((row) => accepted.has(normalizePortalHeader(row[statusHeader])));
};

export const suggestPortalMapping = (headers: string[]): Record<string, string> => {
  const suggestions: Record<string, string> = {};
  const used = new Set<string>();
  PORTAL_SYSTEM_COLUMNS.forEach((column) => {
    const aliases = new Set([column.label, ...column.aliases].map(normalizePortalHeader));
    const source = headers.find((header) => !used.has(header) && aliases.has(normalizePortalHeader(header)));
    if (source) {
      suggestions[column.key] = source;
      used.add(source);
    }
  });
  return suggestions;
};
